/**
 * ===== EXPORT SERVICE - EXPORT COMPLET =====
 * Service complet pour l'export de projets en différents formats;
 */
import Konva from 'konva';
import { logger } from '../utils/ProfessionalLogger';
import jsPDF from 'jspdf';
import JSZip from 'jszip';
import { PhotoboothExportService } from './PhotoboothExportService';
import type { PhotoTemplateService } from './PhotoTemplateService';
import { useAppStore, type AppState, type AppActions } from './StateService';
import { canvasStageRefService } from '../hooks/useCanvasStageRef';
// FIX runtime : imports statiques (require() casse en ESM/Vite → "require is not defined").
import { ImageFilterManager } from '../ImageFilterManager';
import { exportErrorEmitter } from './ExportErrorEmitter';
export interface ExportOptions {
  format: 'png' | 'jpg' | 'webp' | 'pdf' | 'svg' | 'json' | 'photobooth-json';
  quality?: number;
  pixelRatio?: number;
  /** DPI cible pour l'export (ex: 300 pour impression haute qualité) */
  dpi?: number;
  width?: number;
  height?: number;
  backgroundColor?: string;
  filename?: string;
  includeMetadata?: boolean;
}
export interface ExportResult {
  success: boolean;
  data?: string | Blob;
  error?: string;
  size?: number;
  filename?: string;
  warning?: string; // CORRIGÉ PRO: Avertissement pour les exports avec éléments non-vectoriels
}

/**
 * Métadonnées d'export
 */
export interface ExportMetadata {
  title: string;
  author?: string;
  description?: string;
  creator?: string;
  created?: string;
  createdAt?: string;
  version?: string;
  canvas: {
    width: number;
    height: number;
    objectsCount?: number;
    layersCount?: number;
    zoomLevel?: number;
    [key: string]: unknown;
  };
  export?: {
    format?: string;
    quality?: number;
    pixelRatio?: number;
    dpi?: number;
    backgroundColor?: string;
    [key: string]: unknown;
  };
  [key: string]: unknown;
}

export class ExportService {
  private stage: Konva.Stage;
  private defaultOptions: ExportOptions;
  private photoboothExportService?: PhotoboothExportService;

  constructor(stage: Konva.Stage, photoTemplateService?: PhotoTemplateService) {
    this.stage = stage;
    this.defaultOptions = {
      format: 'png',
      quality: 1,
      pixelRatio: 3, // 3x par défaut (≈288 DPI, proche de 300 DPI pour impression haute qualité)
      backgroundColor: '#ffffff',
      includeMetadata: true,
    };
    if (photoTemplateService) {
      this.photoboothExportService = new PhotoboothExportService(stage, photoTemplateService);
    }
  }

  /**
   * Calcule le pixelRatio à partir d'un DPI cible
   *
   * @param dpi - DPI cible (ex: 300 pour impression haute qualité)
   * @param baseDpi - DPI de base de l'écran (par défaut 96 DPI)
   * @returns Le pixelRatio à utiliser pour atteindre le DPI cible
   *
   * @example
   * ```typescript
   * // Pour 300 DPI (impression haute qualité)
   * const pixelRatio = this.calculatePixelRatioFromDPI(300); // ≈ 3.125
   *
   * // Pour 150 DPI (impression standard)
   * const pixelRatio = this.calculatePixelRatioFromDPI(150); // ≈ 1.56
   * ```
   */
  private calculatePixelRatioFromDPI(dpi: number, baseDpi: number = 96): number {
    if (dpi <= 0) {
      logger.warn(`DPI invalide: ${dpi}, utilisation de la valeur par défaut`);
      return this.defaultOptions.pixelRatio || 2;
    }

    const calculatedRatio = dpi / baseDpi;
    // Arrondir à 2 décimales pour éviter les problèmes de précision
    return Math.round(calculatedRatio * 100) / 100;
  }

  /**
   * Calcule le pixelRatio final en tenant compte du DPI ou du pixelRatio explicite
   *
   * @param options - Options d'export
   * @returns Le pixelRatio à utiliser pour l'export
   */
  private getEffectivePixelRatio(options: Partial<ExportOptions>): number {
    // Si un DPI est spécifié, calculer le pixelRatio depuis le DPI
    if (options.dpi !== undefined && options.dpi > 0) {
      const calculatedRatio = this.calculatePixelRatioFromDPI(options.dpi);
      logger.info(`📐 DPI ${options.dpi} → pixelRatio ${calculatedRatio}`);
      return calculatedRatio;
    }

    // Sinon, utiliser le pixelRatio explicite ou la valeur par défaut
    return options.pixelRatio ?? this.defaultOptions.pixelRatio ?? 2;
  }
  /**
   * Exporte le canvas en image PNG haute résolution
   *
   * @param options - Options d'export (dpi pour haute résolution, ex: 300)
   * @returns Résultat de l'export avec l'image PNG
   *
   * @example
   * ```typescript
   * // Export standard (192 DPI approximatif)
   * await exportService.exportToPNG();
   *
   * // Export haute résolution 300 DPI
   * await exportService.exportToPNG({ dpi: 300 });
   *
   * // Export avec pixelRatio explicite
   * await exportService.exportToPNG({ pixelRatio: 4 });
   * ```
   */
  async exportToPNG(options: Partial<ExportOptions> = {}): Promise<ExportResult> {
    useAppStore.getState().setExporting(true);
    await new Promise(resolve => setTimeout(resolve, 0));
    try {
      const finalOptions = { ...this.defaultOptions, ...options, format: 'png' as const };
      const pixelRatio = this.getEffectivePixelRatio(finalOptions);

      // SEMAINE 3.3: Masquer les guides d'alignement et le Transformer avant l'export
      const guidesLayer = this.stage.findOne('.guidesLayer') as Konva.Layer | null;
      const uiLayer = this.stage.findOne('.uiLayer') as Konva.Layer | null;
      const transformer = this.stage.findOne('Transformer') as Konva.Transformer | null;

      const guidesVisible = guidesLayer?.visible() ?? true;
      const uiLayerVisible = uiLayer?.visible() ?? true;
      const transformerVisible = transformer?.visible() ?? true;

      // Masquer les guides et le Transformer
      if (guidesLayer) guidesLayer.visible(false);
      if (uiLayer) uiLayer.visible(false);
      if (transformer) transformer.visible(false);

      // Vérifier que toutes les images sont chargées avant l'export
      const images = this.stage.find('Image') as Konva.Image[];
      const unloadedImages = images.filter(img => {
        const imageObj = img.image();
        return !imageObj || !(imageObj as HTMLImageElement).complete;
      });

      if (unloadedImages.length > 0) {
        logger.warn(
          `⚠️ ${unloadedImages.length} image(s) non encore chargée(s). L'export peut être incomplet.`
        );
        // Attendre un peu pour que les images se chargent
        await new Promise(resolve => setTimeout(resolve, 500));
      }

      logger.info(
        `📸 Export PNG - pixelRatio: ${pixelRatio}, dimensions: ${this.stage.width()}x${this.stage.height()}`
      );

      const dataUrl = this.stage.toDataURL({
        pixelRatio,
        quality: finalOptions.quality,
      });

      // Restaurer la visibilité des guides et du Transformer
      if (guidesLayer) guidesLayer.visible(guidesVisible);
      if (uiLayer) uiLayer.visible(uiLayerVisible);
      if (transformer) transformer.visible(transformerVisible);

      return {
        success: true,
        data: dataUrl,
        size: this.calculateDataSize(dataUrl),
        filename: finalOptions.filename || this.generateFilename('png'),
      };
    } catch (error) {
      logger.error("Erreur lors de l'export PNG:", error);
      return {
        success: false,
        error: "Erreur lors de l'export PNG",
      };
    } finally {
      useAppStore.getState().setExporting(false);
    }
  }
  /**
   * Exporte le canvas en image JPG haute résolution
   *
   * @param options - Options d'export (dpi pour haute résolution, ex: 300)
   * @returns Résultat de l'export avec l'image JPG
   */
  async exportToJPG(options: Partial<ExportOptions> = {}): Promise<ExportResult> {
    useAppStore.getState().setExporting(true);
    await new Promise(resolve => setTimeout(resolve, 0));
    try {
      const finalOptions = { ...this.defaultOptions, ...options, format: 'jpg' as const };
      const pixelRatio = this.getEffectivePixelRatio(finalOptions);

      // SEMAINE 3.3: Masquer les guides d'alignement et le Transformer avant l'export
      const guidesLayer = this.stage.findOne('.guidesLayer') as Konva.Layer | null;
      const uiLayer = this.stage.findOne('.uiLayer') as Konva.Layer | null;
      const transformer = this.stage.findOne('Transformer') as Konva.Transformer | null;

      const guidesVisible = guidesLayer?.visible() ?? true;
      const uiLayerVisible = uiLayer?.visible() ?? true;
      const transformerVisible = transformer?.visible() ?? true;

      // Masquer les guides et le Transformer
      if (guidesLayer) guidesLayer.visible(false);
      if (uiLayer) uiLayer.visible(false);
      if (transformer) transformer.visible(false);

      // Vérifier que toutes les images sont chargées avant l'export
      const images = this.stage.find('Image') as Konva.Image[];
      const unloadedImages = images.filter(img => {
        const imageObj = img.image();
        return !imageObj || !(imageObj as HTMLImageElement).complete;
      });

      if (unloadedImages.length > 0) {
        logger.warn(
          `⚠️ ${unloadedImages.length} image(s) non encore chargée(s). L'export peut être incomplet.`
        );
        // Attendre un peu pour que les images se chargent
        await new Promise(resolve => setTimeout(resolve, 500));
      }

      logger.info(`📸 Export JPG - pixelRatio: ${pixelRatio}, quality: ${finalOptions.quality}`);

      const dataUrl = this.stage.toDataURL({
        pixelRatio,
        quality: finalOptions.quality,
        mimeType: 'image/jpeg',
      });

      // Restaurer la visibilité des guides et du Transformer
      if (guidesLayer) guidesLayer.visible(guidesVisible);
      if (uiLayer) uiLayer.visible(uiLayerVisible);
      if (transformer) transformer.visible(transformerVisible);

      const result = {
        success: true,
        data: dataUrl,
        size: this.calculateDataSize(dataUrl),
        filename: finalOptions.filename || this.generateFilename('jpg'),
      };

      // PR-09 : Track l'export (en arrière-plan, non bloquant)
      if (result.success && result.size) {
        const exportId = `${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
        // P3 : trackExport non typé sur la classe ; appelé optional + typé manuellement.
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        (this as any).trackExport?.(exportId, 'jpg', result.size).catch((err: unknown) => {
          logger.warn('[ExportService] Erreur non bloquante lors du tracking:', err);
        });
      }

      return result;
    } catch (error) {
      logger.error("Erreur lors de l'export JPG:", error);
      return {
        success: false,
        error: "Erreur lors de l'export JPG",
      };
    } finally {
      useAppStore.getState().setExporting(false);
    }
  }
  /**
   * Exporte le canvas en image WebP haute résolution
   *
   * @param options - Options d'export (dpi pour haute résolution, ex: 300)
   * @returns Résultat de l'export avec l'image WebP
   */
  async exportToWebP(options: Partial<ExportOptions> = {}): Promise<ExportResult> {
    useAppStore.getState().setExporting(true);
    await new Promise(resolve => setTimeout(resolve, 0));
    try {
      const finalOptions = { ...this.defaultOptions, ...options, format: 'webp' as const };
      const pixelRatio = this.getEffectivePixelRatio(finalOptions);

      logger.info(`📸 Export WebP - pixelRatio: ${pixelRatio}, quality: ${finalOptions.quality}`);

      const dataUrl = this.stage.toDataURL({
        pixelRatio,
        quality: finalOptions.quality,
        mimeType: 'image/webp',
      });

      return {
        success: true,
        data: dataUrl,
        size: this.calculateDataSize(dataUrl),
        filename: finalOptions.filename || this.generateFilename('webp'),
      };
    } catch (error) {
      logger.error("Erreur lors de l'export WebP:", error);
      return {
        success: false,
        error: "Erreur lors de l'export WebP",
      };
    } finally {
      useAppStore.getState().setExporting(false);
    }
  }
  /**
   * Exporte le canvas en PDF haute résolution
   *
   * @param options - Options d'export (dpi pour haute résolution, ex: 300)
   * @returns Résultat de l'export avec le PDF
   */
  async exportToPDF(options: Partial<ExportOptions> = {}): Promise<ExportResult> {
    // Signaler le début de l'export
    useAppStore.getState().setExporting(true);
    // Laisser un tick au navigateur pour afficher le spinner
    await new Promise(resolve => setTimeout(resolve, 0));

    try {
      const finalOptions = { ...this.defaultOptions, ...options, format: 'pdf' as const };
      const pixelRatio = this.getEffectivePixelRatio(finalOptions);

      // Obtenir les dimensions en pixels
      const canvasWidthPx = finalOptions.width || this.stage.width();
      const canvasHeightPx = finalOptions.height || this.stage.height();

      // Convertir les pixels en mm (1 pixel ≈ 0.264583 mm à 96 DPI)
      const PIXELS_TO_MM = 0.264583;
      const canvasWidthMm = canvasWidthPx * PIXELS_TO_MM;
      const canvasHeightMm = canvasHeightPx * PIXELS_TO_MM;

      logger.info(
        `📄 Export PDF - pixelRatio: ${pixelRatio}, dimensions: ${canvasWidthMm}x${canvasHeightMm}mm`
      );

      // Créer le PDF avec les dimensions en mm
      const pdf = new jsPDF({
        orientation: canvasWidthMm > canvasHeightMm ? 'landscape' : 'portrait',
        unit: 'mm',
        format: [canvasWidthMm, canvasHeightMm],
      });

      // SEMAINE 3.3: Masquer les guides d'alignement et le Transformer avant l'export
      const guidesLayer = this.stage.findOne('.guidesLayer') as Konva.Layer | null;
      const uiLayer = this.stage.findOne('.uiLayer') as Konva.Layer | null;
      const transformer = this.stage.findOne('Transformer') as Konva.Transformer | null;

      const guidesVisible = guidesLayer?.visible() ?? true;
      const uiLayerVisible = uiLayer?.visible() ?? true;
      const transformerVisible = transformer?.visible() ?? true;

      // Masquer les guides et le Transformer
      if (guidesLayer) guidesLayer.visible(false);
      if (uiLayer) uiLayer.visible(false);
      if (transformer) transformer.visible(false);

      // AMÉLIORATION: Export PDF vectoriel - Extraire le texte et les formes simples
      const vectorElements = this.extractVectorElements();

      // Exporter l'image avec le pixelRatio calculé pour haute résolution (fallback pour images/filtres)
      const dataUrl = this.stage.toDataURL({
        pixelRatio,
      });

      // Restaurer la visibilité des guides et du Transformer
      if (guidesLayer) guidesLayer.visible(guidesVisible);
      if (uiLayer) uiLayer.visible(uiLayerVisible);
      if (transformer) transformer.visible(transformerVisible);

      // AMÉLIORATION: Ajouter les éléments vectoriels (texte et formes simples) au PDF
      if (vectorElements.texts.length > 0 || vectorElements.shapes.length > 0) {
        // Ajouter le texte comme vecteur (sélectionnable/recherchable)
        vectorElements.texts.forEach(textEl => {
          const xMm = textEl.x * PIXELS_TO_MM;
          const yMm = textEl.y * PIXELS_TO_MM;
          const fontSizeMm = (textEl.fontSize * PIXELS_TO_MM) / 96; // Convertir en mm (96 DPI de base)

          // Configurer la police
          pdf.setFont(textEl.fontFamily || 'helvetica', textEl.fontStyle || 'normal');
          pdf.setFontSize(fontSizeMm);
          pdf.setTextColor(textEl.color || '#000000');

          // Ajouter le texte (jsPDF gère l'alignement)
          const align = textEl.align || 'left';
          if (align === 'center') {
            pdf.text(textEl.text, canvasWidthMm / 2, yMm, { align: 'center' });
          } else if (align === 'right') {
            pdf.text(textEl.text, canvasWidthMm - xMm, yMm, { align: 'right' });
          } else {
            pdf.text(textEl.text, xMm, yMm);
          }
        });

        // Ajouter les formes simples comme vecteurs (rectangles, cercles)
        vectorElements.shapes.forEach(shape => {
          if (shape.type === 'rect') {
            const xMm = shape.x * PIXELS_TO_MM;
            const yMm = shape.y * PIXELS_TO_MM;
            const widthMm = shape.width * PIXELS_TO_MM;
            const heightMm = shape.height * PIXELS_TO_MM;

            // Dessiner un rectangle vectoriel
            if (shape.fill) {
              pdf.setFillColor(shape.fill);
              pdf.rect(xMm, yMm, widthMm, heightMm, 'F'); // 'F' = fill
            }
            if (shape.stroke && shape.strokeWidth) {
              pdf.setDrawColor(shape.stroke);
              pdf.setLineWidth((shape.strokeWidth * PIXELS_TO_MM) / 96);
              pdf.rect(xMm, yMm, widthMm, heightMm, 'D'); // 'D' = draw
            }
          } else if (shape.type === 'circle') {
            const xMm = shape.x * PIXELS_TO_MM;
            const yMm = shape.y * PIXELS_TO_MM;
            const radiusMm = (shape.radius || shape.width / 2) * PIXELS_TO_MM;

            // Dessiner un cercle vectoriel
            if (shape.fill) {
              pdf.setFillColor(shape.fill);
              pdf.circle(xMm + radiusMm, yMm + radiusMm, radiusMm, 'F');
            }
            if (shape.stroke && shape.strokeWidth) {
              pdf.setDrawColor(shape.stroke);
              pdf.setLineWidth((shape.strokeWidth * PIXELS_TO_MM) / 96);
              pdf.circle(xMm + radiusMm, yMm + radiusMm, radiusMm, 'D');
            }
          }
        });
      }

      // Ajouter l'image bitmap en arrière-plan (pour les images et filtres complexes)
      // Utiliser un mode de fusion pour que le texte vectoriel soit visible par-dessus
      pdf.addImage(dataUrl, 'PNG', 0, 0, canvasWidthMm, canvasHeightMm, undefined, 'FAST');

      // Ajouter les métadonnées si demandé
      if (finalOptions.includeMetadata) {
        this.addMetadataToPDF(pdf, finalOptions);
      }

      // Générer le PDF
      const pdfBlob = pdf.output('blob');
      return {
        success: true,
        data: pdfBlob,
        size: pdfBlob.size,
        filename: finalOptions.filename || this.generateFilename('pdf'),
      };
    } catch (error) {
      logger.error("Erreur lors de l'export PDF:", error);
      return {
        success: false,
        error: "Erreur lors de l'export PDF",
      };
    } finally {
      // Toujours nettoyer l'état d'export, même en cas d'erreur
      useAppStore.getState().setExporting(false);
    }
  }
  /**
   * AMÉLIORATION: Extrait les éléments vectoriels (texte et formes simples) du canvas
   * Pour l'export PDF vectoriel
   */
  private extractVectorElements(): {
    texts: Array<{
      text: string;
      x: number;
      y: number;
      fontSize: number;
      fontFamily: string;
      fontStyle: string;
      color: string;
      align: string;
    }>;
    shapes: Array<{
      type: 'rect' | 'circle';
      x: number;
      y: number;
      width: number;
      height: number;
      radius?: number;
      fill?: string;
      stroke?: string;
      strokeWidth?: number;
    }>;
  } {
    const texts: Array<{
      text: string;
      x: number;
      y: number;
      fontSize: number;
      fontFamily: string;
      fontStyle: string;
      color: string;
      align: string;
    }> = [];

    const shapes: Array<{
      type: 'rect' | 'circle';
      x: number;
      y: number;
      width: number;
      height: number;
      radius?: number;
      fill?: string;
      stroke?: string;
      strokeWidth?: number;
    }> = [];

    // Parcourir tous les nœuds du stage
    this.stage.find('*').forEach((node: Konva.Node) => {
      // Ignorer les layers UI et les guides
      if (
        node.getClassName() === 'Layer' &&
        (node.name() === 'uiLayer' ||
          node.name() === 'guidesLayer' ||
          node.name() === 'selection-ui-layer')
      ) {
        return;
      }

      // Ignorer le transformer et les guides
      if (
        node.getClassName() === 'Transformer' ||
        node.getAttr('gridLine') === true ||
        node.name() === 'selection-rect'
      ) {
        return;
      }

      // Extraire le texte
      if (node instanceof Konva.Text) {
        const absPos = node.absolutePosition();
        const fill = node.fill();
        texts.push({
          text: node.text(),
          x: absPos.x,
          y: absPos.y,
          fontSize: node.fontSize(),
          fontFamily: node.fontFamily() || 'helvetica',
          fontStyle: node.fontStyle() || 'normal',
          color: typeof fill === 'string' ? fill : '#000000',
          align: node.align() || 'left',
        });
      }

      // Extraire les formes simples (rectangles et cercles sans filtres)
      if (node instanceof Konva.Rect && !node.filters()?.length) {
        const absPos = node.absolutePosition();
        const fill = node.fill();
        const stroke = node.stroke();
        shapes.push({
          type: 'rect',
          x: absPos.x,
          y: absPos.y,
          width: node.width() * node.scaleX(),
          height: node.height() * node.scaleY(),
          fill: typeof fill === 'string' ? fill : undefined,
          stroke: typeof stroke === 'string' ? stroke : undefined,
          strokeWidth: node.strokeWidth() || 0,
        });
      }

      if (node instanceof Konva.Circle && !node.filters()?.length) {
        const absPos = node.absolutePosition();
        const fill = node.fill();
        const stroke = node.stroke();
        shapes.push({
          type: 'circle',
          x: absPos.x,
          y: absPos.y,
          width: node.width() * node.scaleX(),
          height: node.height() * node.scaleY(),
          radius: node.radius() * node.scaleX(),
          fill: typeof fill === 'string' ? fill : undefined,
          stroke: typeof stroke === 'string' ? stroke : undefined,
          strokeWidth: node.strokeWidth() || 0,
        });
      }
    });

    return { texts, shapes };
  }

  /**
   * Détecte les éléments non-vectoriels dans le canvas
   * CORRIGÉ PRO: Vérifie la présence de filtres et d'images qui ne peuvent pas être vectorisés
   */
  private detectNonVectorElements(): {
    hasFilters: boolean;
    hasImages: boolean;
    filterCount: number;
    imageCount: number;
  } {
    let hasFilters = false;
    let hasImages = false;
    let filterCount = 0;
    let imageCount = 0;

    // Parcourir tous les nœuds du stage
    this.stage.find('*').forEach((node: Konva.Node) => {
      // Détecter les images
      if (node instanceof Konva.Image) {
        hasImages = true;
        imageCount++;

        // Détecter les filtres sur les images
        const filters = node.filters();
        if (filters && filters.length > 0) {
          hasFilters = true;
          filterCount++;
        }

        // Vérifier aussi les filtres personnalisés via ImageFilterManager
        try {
          if (ImageFilterManager.hasFilters(node)) {
            hasFilters = true;
            filterCount++;
          }
        } catch (_e) {
          // ImageFilterManager peut ne pas être disponible
        }
      }
    });

    return { hasFilters, hasImages, filterCount, imageCount };
  }

  /**
   * Exporte le canvas en SVG (fallback PNG haute résolution)
   * CORRIGÉ PRO: Détecte et avertit l'utilisateur des éléments non-vectoriels
   *
   * @param options - Options d'export (dpi pour haute résolution, ex: 300)
   * @returns Résultat de l'export avec l'image (PNG car SVG non supporté nativement)
   */
  async exportToSVG(options: Partial<ExportOptions> = {}): Promise<ExportResult> {
    useAppStore.getState().setExporting(true);
    await new Promise(resolve => setTimeout(resolve, 0));
    try {
      const finalOptions = { ...this.defaultOptions, ...options, format: 'svg' as const };
      const pixelRatio = this.getEffectivePixelRatio(finalOptions);

      // Détecter les éléments non-vectoriels
      const nonVectorInfo = this.detectNonVectorElements();

      // Avertir l'utilisateur si des éléments non-vectoriels sont présents
      if (nonVectorInfo.hasFilters || nonVectorInfo.hasImages) {
        const warnings: string[] = [];

        if (nonVectorInfo.hasFilters) {
          warnings.push(
            `${nonVectorInfo.filterCount} image(s) avec filtres détectée(s). Les filtres (blur, brightness, etc.) ne peuvent pas être vectorisés et seront convertis en bitmap dans l'export SVG.`
          );
        }

        if (nonVectorInfo.hasImages) {
          warnings.push(
            `${nonVectorInfo.imageCount} image(s) détectée(s). Les images seront converties en bitmap dans l'export SVG, ce qui peut augmenter la taille du fichier et réduire la qualité à l'impression grand format.`
          );
        }

        const warningMessage = warnings.join('\n');
        logger.warn('⚠️ Export SVG - Éléments non-vectoriels détectés:', warningMessage);

        // Émettre un événement personnalisé pour que l'UI puisse afficher un avertissement
        const warningEvent = new CustomEvent('export:svg-warning', {
          detail: {
            message: warningMessage,
            hasFilters: nonVectorInfo.hasFilters,
            hasImages: nonVectorInfo.hasImages,
            filterCount: nonVectorInfo.filterCount,
            imageCount: nonVectorInfo.imageCount,
          },
          bubbles: true,
        });
        document.dispatchEvent(warningEvent);
      }

      // Konva ne supporte pas nativement l'export SVG
      // On utilise un export PNG haute résolution comme fallback
      logger.warn(
        "L'export SVG natif n'est pas supporté. Utilisation d'un export PNG haute résolution."
      );

      const dataUrl = this.stage.toDataURL({
        pixelRatio,
      });

      return {
        success: true,
        data: dataUrl,
        size: dataUrl.length,
        filename: finalOptions.filename || this.generateFilename('png'),
        // Ajouter les informations sur les éléments non-vectoriels dans le résultat
        warning:
          nonVectorInfo.hasFilters || nonVectorInfo.hasImages
            ? "L'export contient des éléments non-vectoriels (filtres ou images) qui seront convertis en bitmap."
            : undefined,
      };
    } catch (error) {
      logger.error("Erreur lors de l'export SVG:", error);
      return {
        success: false,
        error: "L'export SVG n'est pas supporté. Utilisez PNG ou PDF.",
      };
    } finally {
      useAppStore.getState().setExporting(false);
    }
  }
  /**
   * Exporte les données JSON du canvas;
   */
  async exportToJSON(options: Partial<ExportOptions> = {}): Promise<ExportResult> {
    useAppStore.getState().setExporting(true);
    await new Promise(resolve => setTimeout(resolve, 0));
    try {
      const finalOptions = { ...this.defaultOptions, ...options, format: 'json' as const };
      const jsonString = this.stage.toJSON();
      // Parser et ajouter les métadonnées si demandé
      interface ExportMetadata {
        title?: string;
        author?: string;
        created?: string;
        version?: string;
        [key: string]: unknown;
      }
      interface ExportDataWithMetadata {
        metadata?: ExportMetadata;
        canvas?: Record<string, unknown>;
        [key: string]: unknown;
      }
      let exportData: ExportDataWithMetadata = JSON.parse(jsonString);
      if (finalOptions.includeMetadata) {
        exportData = {
          ...exportData,
          metadata: this.generateMetadata(finalOptions),
        };
      }
      const finalJsonString = JSON.stringify(exportData, null, 2);
      return {
        success: true,
        data: finalJsonString,
        size: finalJsonString.length,
        filename: finalOptions.filename || this.generateFilename('json'),
      };
    } catch (error) {
      logger.error("Erreur lors de l'export JSON:", error);
      return {
        success: false,
        error: "Erreur lors de l'export JSON",
      };
    } finally {
      useAppStore.getState().setExporting(false);
    }
  }
  /**
   * Exporte le canvas en format JSON runtime photobooth
   * CORRIGÉ: Ajoute aussi l'option d'exporter les fichiers séparés (overlay.png, background.jpg)
   */
  async exportToPhotoboothJSON(options: Partial<ExportOptions> = {}): Promise<ExportResult> {
    useAppStore.getState().setExporting(true);
    await new Promise(resolve => setTimeout(resolve, 0));
    try {
      if (!this.photoboothExportService) {
        // Créer le service si nécessaire
        this.photoboothExportService = new PhotoboothExportService(this.stage);
      }

      // Si l'option exportSeparatedFiles est activée, exporter les fichiers séparés
      interface ExtendedExportOptions extends ExportOptions {
        exportSeparatedFiles?: boolean;
      }
      if ((options as ExtendedExportOptions).exportSeparatedFiles) {
        const separatedFiles = await this.photoboothExportService.exportSeparatedFiles({
          dpi: options.dpi || 300,
          quality: options.quality || 0.95,
        });

        // Créer un ZIP avec les 3 fichiers (overlay.png, background.jpg, coordinates.json)
        const zip = new JSZip();

        // Ajouter les fichiers au ZIP
        zip.file('overlay.png', separatedFiles.overlay);
        zip.file('background.jpg', separatedFiles.background);
        zip.file('coordinates.json', separatedFiles.coordinates);

        // Générer le ZIP
        const zipBlob = await zip.generateAsync({ type: 'blob' });

        logger.info(
          '✅ Export séparé généré et compressé en ZIP: overlay.png, background.jpg, coordinates.json'
        );

        // Télécharger le ZIP
        const zipUrl = URL.createObjectURL(zipBlob);
        const zipLink = document.createElement('a');
        zipLink.href = zipUrl;
        zipLink.download = options.filename || this.generateFilename('photobooth-export.zip');
        document.body.appendChild(zipLink);
        zipLink.click();
        document.body.removeChild(zipLink);
        URL.revokeObjectURL(zipUrl);

        return {
          success: true,
          data: zipBlob,
          size: zipBlob.size,
          filename: options.filename || this.generateFilename('photobooth-export.zip'),
        };
      }

      // Export JSON classique (compatibilité)
      // CORRIGÉ: exportToJSONString est maintenant async
      const jsonString = await this.photoboothExportService.exportToJSONString(2);
      const filename = options.filename || this.generateFilename('photobooth.json');

      return {
        success: true,
        data: jsonString,
        size: jsonString.length,
        filename,
      };
    } catch (error) {
      logger.error("Erreur lors de l'export photobooth JSON:", error);

      // Message d'erreur utilisateur-friendly
      let errorMessage = "Erreur lors de l'export photobooth JSON";
      if (error instanceof Error) {
        if (error.message.includes('Template invalide')) {
          errorMessage = `Template invalide : ${error.message.split('\n').slice(1).join(', ')}`;
        } else {
          errorMessage = error.message;
        }
      }

      // CORRIGÉ: Émettre un événement pour ToastProvider
      try {
        exportErrorEmitter.emitExportError({
          type: 'error',
          message: errorMessage,
          duration: 8000,
        });
      } catch (_e) {
        // EventEmitter peut ne pas être disponible, continuer quand même
      }

      return {
        success: false,
        error: errorMessage,
      };
    }
  }

  /**
   * Exporte les fichiers séparés pour photobooth (overlay.png, background.jpg, coordinates.json)
   * CORRIGÉ: Nouvelle méthode publique pour exporter les fichiers séparés
   */
  async exportPhotoboothSeparatedFiles(options: Partial<ExportOptions> = {}): Promise<{
    overlay: Blob;
    background: Blob;
    coordinates: string;
  }> {
    useAppStore.getState().setExporting(true);
    await new Promise(resolve => setTimeout(resolve, 0));
    try {
      if (!this.photoboothExportService) {
        this.photoboothExportService = new PhotoboothExportService(this.stage);
      }

      return await this.photoboothExportService.exportSeparatedFiles({
        dpi: options.dpi || 300,
        quality: options.quality || 0.95,
      });
    } finally {
      useAppStore.getState().setExporting(false);
    }
  }

  /**
   * Exporte selon le format spécifié
   */
  async export(options: ExportOptions): Promise<ExportResult> {
    switch (options.format) {
      case 'png':
        return this.exportToPNG(options);
      case 'jpg':
        return this.exportToJPG(options);
      case 'webp':
        return this.exportToWebP(options);
      case 'pdf':
        return this.exportToPDF(options);
      case 'svg':
        return this.exportToSVG(options);
      case 'json':
        return this.exportToJSON(options);
      case 'photobooth-json':
        return this.exportToPhotoboothJSON(options);
      default:
        return {
          success: false,
          error: `Format non supporté: ${options.format}`,
        };
    }
  }
  /**
   * Méthode statique pour exporter un projet depuis le store Zustand
   * Utilise canvasStageRefService pour obtenir le stage
   *
   * @param store - Store Zustand complet
   * @param format - Format d'export
   * @param options - Options d'export (dpi, quality, pixelRatio)
   */
  static async exportProject(
    store: AppState & AppActions,
    format: ExportOptions['format'],
    options?: { dpi?: number; quality?: number; pixelRatio?: number }
  ): Promise<ExportResult> {
    // Obtenir le stage depuis le service global
    const stage = canvasStageRefService.getStage();
    if (!stage) {
      logger.error("⚠️ Stage non disponible pour l'export");
      return {
        success: false,
        error: 'Stage non disponible. Le canvas doit être monté pour exporter.',
      };
    }

    // Créer une instance temporaire d'ExportService
    const exportService = new ExportService(stage);

    // Convertir le format en ExportOptions
    const exportOptions: ExportOptions = {
      format,
      ...options,
    };

    // Exporter selon le format
    return exportService.export(exportOptions);
  }

  /**
   * Télécharge le fichier exporté
   */
  downloadExport(result: ExportResult): void {
    if (!result.success || !result.data) {
      logger.error('Impossible de télécharger:', result.error);
      return;
    }
    const link = document.createElement('a');
    link.download = result.filename || 'export';
    if (result.data instanceof Blob) {
      link.href = URL.createObjectURL(result.data);
    } else {
      link.href = result.data;
    }
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    if (result.data instanceof Blob) {
      URL.revokeObjectURL(link.href);
    }
  }
  /**
   * Obtient les métadonnées du canvas;
   */
  getCanvasMetadata(): {
    width: number;
    height: number;
    objectsCount: number;
    layersCount: number;
    zoomLevel: number;
  } {
    return {
      width: this.stage.width(),
      height: this.stage.height(),
      objectsCount: this.stage.find('Shape').length,
      layersCount: this.stage.children.length,
      zoomLevel: this.stage.scaleX(),
    };
  }
  /**
   * Calcule la taille des données;
   */
  private calculateDataSize(data: string): number {
    return new Blob([data]).size;
  }
  /**
   * Génère un nom de fichier;
   */
  private generateFilename(extension: string): string {
    const timestamp = new Date().toISOString().replace(/[:.]/g, '-');
    return `snapbooth-export-${timestamp}.${extension}`;
  }
  /**
   * Ajoute les métadonnées au PDF;
   */
  private addMetadataToPDF(pdf: jsPDF, options: ExportOptions): void {
    const metadata = this.generateMetadata(options);
    pdf.setProperties({
      title: metadata.title,
      subject: metadata.description,
      author: metadata.creator,
      creator: metadata.creator,
    });
  }
  /**
   * Ajoute les métadonnées au SVG;
   */
  private addMetadataToSVG(svgString: string, options: ExportOptions): string {
    const metadata = this.generateMetadata(options);
    const metadataElement = `
      <metadata>
        <title>${metadata.title}</title>
        <description>${metadata.description}</description>
        <creator>${metadata.creator}</creator>
        <created>${metadata.createdAt}</created>
      </metadata>
    `;
    return svgString.replace('<svg', `${metadataElement}\n<svg`);
  }
  /**
   * Génère les métadonnées;
   */
  private generateMetadata(options: ExportOptions): ExportMetadata {
    const metadata = this.getCanvasMetadata();
    return {
      title: options.filename || 'Snapbooth Export',
      description: `Export Snapbooth - ${metadata.objectsCount} objets, ${metadata.layersCount} couches`,
      creator: 'Snapbooth Template Editor',
      createdAt: new Date().toISOString(),
      canvas: {
        width: metadata.width,
        height: metadata.height,
        objectsCount: metadata.objectsCount,
        layersCount: metadata.layersCount,
        zoomLevel: metadata.zoomLevel,
      },
      export: {
        format: options.format,
        quality: options.quality,
        pixelRatio: options.pixelRatio,
        dpi: options.dpi,
        backgroundColor: options.backgroundColor,
      },
    };
  }
  /**
   * Nettoie les ressources;
   */
  destroy(): void {
    this.stage = null as unknown as Konva.Stage;
  }
}
